// ref_summary, ref_reason, and plan_of_action are HTML fields from the legacy TinyMCE
// rich text editor and cannot be guaranteed safe. Backend-to-markdown conversion was
// considered but ruled out — tags like <table>, <img>, <span>, and <div> do not
// translate cleanly and would lose content fidelity. DOMPurify sanitizes on the
// frontend before render as defence-in-depth.
import DOMPurify from 'dompurify';
import { ScrollArea } from '../ui/ScrollArea';
import { PhiProvider } from '../../contexts/PhiContext';
import { Field } from '../ui/Field';
import { ReferralDocument, ReferralDocumentHeader, ReferralDocumentSection } from './ReferralDocument';
import { LoadingSpinner } from '../LoadingSpinner';

const normalizeType = (type) => (type || '').toLowerCase();

// data / loading / error are owned by ReferralDrawer so the detail endpoint is
// fetched once per drawer open and shared with sibling tabs (e.g. Member
// Details). Previously this component drove its own fetch, which caused a
// redundant network call every time the user toggled away and back.
function ReferralInfoPanel({ caseId, referralType, referralDate, providerName, data, loading = false, error = null }) {
  const normalized = normalizeType(referralType);
  const isClaim = normalized === 'claim';
  const isProvider = normalized === 'provider';
  const planItems = Array.isArray(data?.plan_of_action_items) ? data.plan_of_action_items : [];
  const planHtml = planItems.length === 0 ? data?.plan_of_action_html : null;
  const maskedFields = data?.phi_masked_fields || [];
  const hasTin = (data?.identifiers?.billing_tin !== null && data?.identifiers?.billing_tin !== undefined) || maskedFields.includes('billing_tin');
  const hasNpi = (data?.identifiers?.npi !== null && data?.identifiers?.npi !== undefined) || maskedFields.includes('npi');
  const isMember = !isClaim && !isProvider;
  const erDateRange = data?.er_date_range || null;
  const erDateRangeHasData = !!erDateRange?.start && !!erDateRange?.end;


  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header — height matches the comments column header (w-7 h-7 bordered button + text-sm label) */}
      <div className="flex items-center gap-1 mb-3">
        <span className="text-sm font-semibold text-foreground">Referral {caseId}</span>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-full min-h-[300px]">
            <LoadingSpinner label="Loading referral details" className="mx-auto" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full min-h-[300px]">
            <p className="text-sm text-destructive">
              Failed to load referral details.
            </p>
          </div>
        ) : isClaim ? (
          /* ── Claim layout ── */
          <ReferralDocument caseId={caseId}>
            <ReferralDocumentHeader fields={[
              { label: 'Referral ID', value: caseId, large: true },
              { label: 'Publish Date', value: referralDate },
              { label: 'Category', value: referralType, capitalize: true },
              { label: 'Provider Name', value: providerName },
            ]} />

            {data?.ref_summary && (
              <ReferralDocumentSection title="Referral Overview">
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.ref_summary) }} />
              </ReferralDocumentSection>
            )}

            {data?.ref_reason && (
              <ReferralDocumentSection title="Referral Reason">
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.ref_reason) }} />
              </ReferralDocumentSection>
            )}

            <ReferralDocumentSection title="Code Definitions">
              {Array.isArray(data?.code_definitions) && data.code_definitions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-[#002471]" data-testid="code-definitions-table">
                    <thead>
                      <tr className="text-left bg-[#002471] text-white">
                        <th className="py-2 px-3 font-semibold whitespace-nowrap">CODE TYPE</th>
                        <th className="py-2 px-3 font-semibold whitespace-nowrap">CODE</th>
                        <th className="py-2 px-3 font-semibold">DESCRIPTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.code_definitions.map((row, i) => (
                        <PhiProvider maskedFields={row.phi_masked_fields ?? []} key={i}>
                          <tr className="border-t border-[#002471] even:bg-[#f1f6f9]">
                            <td className="py-1.5 px-3 align-top">{row.code_type}</td>
                            <td className="py-1.5 px-3 align-top">
                              <Field name="code" value={row.code} />
                            </td>
                            <td className="py-1.5 px-3 align-top">
                              <Field name="description" value={row.description} />
                            </td>
                          </tr>
                        </PhiProvider>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No information available.</p>
              )}
            </ReferralDocumentSection>

            <ReferralDocumentSection title="Corrective Action">
              {planItems.length > 0 ? (
                <ol className="list-decimal pl-5 space-y-2 text-sm text-foreground">
                  {planItems.map((item, i) => (
                    <li key={i}>
                      <span className="font-bold">{item.display}</span>
                      {item.notes && (
                        <div className="mt-1 text-foreground/90 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.notes) }} />
                      )}
                    </li>
                  ))}
                </ol>
              ) : planHtml ? (
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(planHtml) }} />
              ) : (
                <p className="text-sm text-muted-foreground">No information available.</p>
              )}
            </ReferralDocumentSection>
          </ReferralDocument>
        ) : isProvider ? (
          /* ── Provider layout ── */
          <ReferralDocument caseId={caseId}>
            <ReferralDocumentHeader fields={[
              { label: 'Referral ID', value: caseId, large: true },
              { label: 'Publish Date', value: referralDate },
              { label: 'Category', value: data?.model_type_name || referralType, capitalize: true },
              { label: 'Provider Name', value: providerName },
            ]} />

            {/*
              Client Exposure: source field not yet confirmed. Section kept as an empty shell
              so the layout is preserved. Once the correct source column is identified,
              restore the outer guard: Array.isArray(data?.client_exposure) && data.client_exposure.length > 0
            */}
            <ReferralDocumentSection title="Client Exposure">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-[#002471]" data-testid="client-exposure-table">
                  <thead>
                    <tr className="text-left bg-[#002471] text-white">
                      <th className="py-2 px-3 font-semibold whitespace-nowrap">PROVIDER NAME</th>
                      <th className="py-2 px-3 font-semibold whitespace-nowrap">NTWK</th>
                      <th className="py-2 px-3 font-semibold whitespace-nowrap">SPECIALTY</th>
                      <th className="py-2 px-3 font-semibold whitespace-nowrap">CITY, ST</th>
                      <th className="py-2 px-3 font-semibold text-right">MBR</th>
                      <th className="py-2 px-3 font-semibold text-right">CLM</th>
                      <th className="py-2 px-3 font-semibold text-right whitespace-nowrap">MIN DOS</th>
                      <th className="py-2 px-3 font-semibold text-right whitespace-nowrap">MAX DOS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(data?.client_exposure) && data.client_exposure.map((row, i) => (
                      <tr key={i} className="border-t border-[#002471] even:bg-[#f1f6f9]">
                        <td className="py-1.5 px-3 align-top">{row.provider_name}</td>
                        <td className="py-1.5 px-3 align-top">{row.ntwk}</td>
                        <td className="py-1.5 px-3 align-top">{row.specialty}</td>
                        <td className="py-1.5 px-3 align-top">{row.city_st}</td>
                        <td className="py-1.5 px-3 align-top text-right">{row.mbr}</td>
                        <td className="py-1.5 px-3 align-top text-right">{row.clm}</td>
                        <td className="py-1.5 px-3 align-top text-right">{row.min_dos}</td>
                        <td className="py-1.5 px-3 align-top text-right">{row.max_dos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ReferralDocumentSection>

            {data?.ref_summary && (
              <ReferralDocumentSection title="Referral Summary">
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.ref_summary) }} />
              </ReferralDocumentSection>
            )}

            {data?.ref_reason && (
              <ReferralDocumentSection title="Referral Reason">
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.ref_reason) }} />
              </ReferralDocumentSection>
            )}

            <ReferralDocumentSection title="Code Definitions">
              {Array.isArray(data?.code_definitions) && data.code_definitions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-[#002471]" data-testid="code-definitions-table">
                    <thead>
                      <tr className="text-left bg-[#002471] text-white">
                        <th className="py-2 px-3 font-semibold whitespace-nowrap">CODE TYPE</th>
                        <th className="py-2 px-3 font-semibold whitespace-nowrap">CODE</th>
                        <th className="py-2 px-3 font-semibold">DESCRIPTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.code_definitions.map((row, i) => (
                        <PhiProvider maskedFields={row.phi_masked_fields ?? []} key={i}>
                          <tr className="border-t border-[#002471] even:bg-[#f1f6f9]">
                            <td className="py-1.5 px-3 align-top">{row.code_type}</td>
                            <td className="py-1.5 px-3 align-top">
                              <Field name="code" value={row.code} />
                            </td>
                            <td className="py-1.5 px-3 align-top">
                              <Field name="description" value={row.description} />
                            </td>
                          </tr>
                        </PhiProvider>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No information available.</p>
              )}
            </ReferralDocumentSection>

            <ReferralDocumentSection title="Identifiers">
              <PhiProvider maskedFields={data?.phi_masked_fields}>
                {hasTin || hasNpi ? (
                  <p className="text-sm text-foreground">
                    {hasTin && (
                      <>
                        <span>TIN: </span>
                        <Field name="billing_tin" value={data?.identifiers?.billing_tin} data-testid="info-billing-tin" />
                      </>
                    )}
                    {hasTin && hasNpi && <span>, </span>}
                    {hasNpi && (
                      <>
                        <span>NPI: </span>
                        <Field name="npi" value={data?.identifiers?.npi} data-testid="info-npi" />
                      </>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">No information available.</p>
                )}
              </PhiProvider>
            </ReferralDocumentSection>

            <ReferralDocumentSection title="Restrictions">
              {data?.restrictions ? (
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.restrictions) }} />
              ) : (
                <p className="text-sm text-muted-foreground">No information available.</p>
              )}
            </ReferralDocumentSection>

            <ReferralDocumentSection title="Corrective Action">
              {planItems.length > 0 ? (
                <ol className="list-decimal pl-5 space-y-2 text-sm text-foreground">
                  {planItems.map((item, i) => (
                    <li key={i}>
                      <span className="font-bold">{item.display}</span>
                      {item.notes && (
                        <div className="mt-1 text-foreground/90 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.notes) }} />
                      )}
                    </li>
                  ))}
                </ol>
              ) : planHtml ? (
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(planHtml) }} />
              ) : (
                <p className="text-sm text-muted-foreground">No information available.</p>
              )}
            </ReferralDocumentSection>
          </ReferralDocument>
        ) : isMember ? (
          /* ── Member layout (all member models: ER, Substance Abuse, generic) ── */
          <ReferralDocument caseId={caseId}>
            <ReferralDocumentHeader fields={[
              { label: 'Referral ID', value: caseId, large: true },
              { label: 'Publish Date', value: referralDate },
              { label: 'Category', value: data?.model_type_name || referralType, capitalize: true },
              { label: 'Provider Name', value: providerName },
              // ER-specific header fields: only rendered when er_date_range is non-null (ER referrals only)
              // ER Paid: pending backend field from data engineering — show empty until available
              ...(erDateRange ? [
                { label: 'ER Date Range', value: erDateRangeHasData ? `${erDateRange.start} – ${erDateRange.end}` : '' },
                { label: 'ER Paid', value: '' },
              ] : []),
            ]} />

            {data?.ref_summary && (
              <ReferralDocumentSection title="Referral Summary">
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.ref_summary) }} />
              </ReferralDocumentSection>
            )}

            {data?.ref_reason && (
              <ReferralDocumentSection title="Referral Reason">
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.ref_reason) }} />
              </ReferralDocumentSection>
            )}

            <ReferralDocumentSection title="Corrective Action">
              {planItems.length > 0 ? (
                <ol className="list-decimal pl-5 space-y-2 text-sm text-foreground">
                  {planItems.map((item, i) => (
                    <li key={i}>
                      <span className="font-bold">{item.display}</span>
                      {item.notes && (
                        <div className="mt-1 text-foreground/90 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.notes) }} />
                      )}
                    </li>
                  ))}
                </ol>
              ) : planHtml ? (
                <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(planHtml) }} />
              ) : (
                <p className="text-sm text-muted-foreground">No information available.</p>
              )}
            </ReferralDocumentSection>
          </ReferralDocument>
        ) : (
          <div className="text-sm text-gray-500 p-4">Unknown referral type</div>
        )}
      </ScrollArea>
    </div>
  );
}

export { ReferralInfoPanel };